// src/mobile_pages/system/ProjectModify.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../api/axios";
import { Settings, MapPin, Calendar, Layers, Save } from "lucide-react";

// 모듈 목록
const MODULE_OPTIONS = [
  { key: "overview", label: "사업개요" },
  { key: "permit", label: "인허가" },
  { key: "contract", label: "계약/비용관리" },
  { key: "design", label: "설계관리" },
  { key: "material", label: "자재관리" },
  { key: "process", label: "공정관리" },
  { key: "construction", label: "시공관리" },
  { key: "quality", label: "품질관리" },
  { key: "safety", label: "안전관리" },
  { key: "meeting", label: "회의∙공지" },
  { key: "approval", label: "전자결재" },
  { key: "document", label: "자료실" },
  { key: "pcmodule", label: "PCModule" },
  { key: "fourd", label: "4dSimulation" },
];

export default function ProjectModify() {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isSuperuser = user?.is_superuser;
  const projectId = localStorage.getItem("projectId");

  const [project, setProject] = useState({
    id: "",
    name: "",
    start_date: "",
    end_date: "",
    address: "",
    extra_address: "",
    detail_address: "",
    modules_ordered: [],
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const addressLayerRef = React.useRef(null);

  useEffect(() => {
    async function fetchProject() {
      try {
        setLoading(true);
        const res = await api.get(`/project/info/${projectId}/`);
        setProject({
          ...res.data,
          modules_ordered: Array.isArray(res.data.modules_ordered)
            ? res.data.modules_ordered
            : [],
        });
      } catch (err) {
        console.error("프로젝트 불러오기 실패:", err);
      } finally {
        setLoading(false);
      }
    }
    if (projectId && isSuperuser) fetchProject();
  }, [projectId, isSuperuser]);

  // 권한 체크 (hooks 이후에 배치)
  if (!isSuperuser) {
    return (
      <div className="mobile-page-root">
        <div className="mobile-page-root-inner p-6">
          <div className="section-card p-6 text-center">
            <p className="text-red-600 font-medium">접근 권한이 없습니다.</p>
          </div>
        </div>
      </div>
    );
  }

  // 다음 주소 검색 - 앱 내 모달로 열기
  const openAddressPopup = () => {
    setShowAddressModal(true);

    // 다음 스크립트 로드 확인
    const loadAndEmbed = () => {
      if (!addressLayerRef.current) return;

      new window.daum.Postcode({
        oncomplete: function (data) {
          let addr = data.userSelectedType === "R" ? data.roadAddress : data.jibunAddress;
          let extraAddr = "";

          if (data.userSelectedType === "R") {
            if (data.bname && /[동|로|가]$/g.test(data.bname)) extraAddr += data.bname;
            if (data.buildingName && data.apartment === "Y")
              extraAddr += extraAddr ? `, ${data.buildingName}` : data.buildingName;
            if (extraAddr) extraAddr = ` (${extraAddr})`;
          }

          setProject((prev) => ({
            ...prev,
            address: addr,
            extra_address: extraAddr,
          }));
          setShowAddressModal(false);
        },
        width: "100%",
        height: "100%",
      }).embed(addressLayerRef.current);
    };

    if (!window.daum) {
      const script = document.createElement("script");
      script.src = "//t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js";
      script.onload = () => setTimeout(loadAndEmbed, 100);
      document.body.appendChild(script);
    } else {
      setTimeout(loadAndEmbed, 100);
    }
  };

  const handleSave = async () => {
    if (!project.name) {
      alert("프로젝트 이름을 입력해주세요.");
      return;
    }
    if (!project.start_date || !project.end_date) {
      alert("프로젝트 기간을 입력해주세요.");
      return;
    }

    setSaving(true);
    try {
      await api.put(`project/info/${projectId}/`, {
        ...project,
        modules: project.modules_ordered,
      });
      alert("프로젝트 정보가 수정되었습니다.");
      navigate(`/project/${projectId}`);
    } catch (err) {
      console.error("저장 실패:", err);
      alert("저장에 실패했습니다.");
    } finally {
      setSaving(false);
    }
  };

  const toggleModule = (key) => {
    setProject((prev) => {
      const current = prev.modules_ordered || [];
      return {
        ...prev,
        modules_ordered: current.includes(key)
          ? current.filter((m) => m !== key)
          : [...current, key],
      };
    });
  };

  if (loading) {
    return (
      <div className="mobile-page-root">
        <div className="mobile-page-root-inner p-4">
          <div className="section-card p-6 text-center animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto mb-2"></div>
            <div className="h-3 bg-gray-200 rounded w-3/4 mx-auto"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mobile-page-root">
      <div className="mobile-page-root-inner space-y-3 mobile-bottom-spacing pb-20">
        {/* Header */}
        <div className="section-card p-4">
          <div className="flex items-center gap-2">
            <Settings className="w-5 h-5 text-blue-600" />
            <h1 className="text-base font-bold text-gray-900">프로젝트 수정</h1>
          </div>
          <p className="text-xs text-gray-500 mt-1">프로젝트 정보를 수정합니다.</p>
        </div>

        {/* 기본 정보 */}
        <div className="section-card p-4 space-y-4">
          <h2 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            기본 정보
          </h2>

          {/* 프로젝트 코드 (읽기전용) */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              프로젝트 코드
            </label>
            <input
              type="text"
              value={project.id}
              readOnly
              className="w-full bg-gray-100 border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-500"
            />
          </div>

          {/* 프로젝트 이름 */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              프로젝트 이름 <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={project.name || ""}
              onChange={(e) => setProject((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="프로젝트 이름"
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* 기간 */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                시작일 <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={project.start_date || ""}
                onChange={(e) =>
                  setProject((prev) => ({ ...prev, start_date: e.target.value }))
                }
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                종료일 <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={project.end_date || ""}
                onChange={(e) =>
                  setProject((prev) => ({ ...prev, end_date: e.target.value }))
                }
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* 주소 정보 */}
        <div className="section-card p-4 space-y-3">
          <h2 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
            <MapPin className="w-4 h-4" />
            주소 정보
          </h2>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">주소</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={project.address || ""}
                onChange={(e) =>
                  setProject((prev) => ({ ...prev, address: e.target.value }))
                }
                placeholder="주소"
                className="flex-1 border border-gray-300 rounded-lg px-3 py-2.5 text-sm"
              />
              <button
                type="button"
                onClick={openAddressPopup}
                className="btn-search px-3 py-2 text-sm whitespace-nowrap"
              >
                검색
              </button>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">참고 주소</label>
            <input
              type="text"
              value={project.extra_address || ""}
              readOnly
              className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-500"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">상세 주소</label>
            <input
              type="text"
              value={project.detail_address || ""}
              onChange={(e) =>
                setProject((prev) => ({ ...prev, detail_address: e.target.value }))
              }
              placeholder="상세 주소 입력"
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* 모듈 관리 */}
        <div className="section-card p-4 space-y-3">
          <h2 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
            <Layers className="w-4 h-4" />
            모듈 관리
          </h2>
          <p className="text-xs text-gray-500">사용할 모듈을 선택하세요.</p>

          <div className="grid grid-cols-2 gap-2">
            {MODULE_OPTIONS.map(({ key, label }) => {
              const isChecked = project.modules_ordered?.includes(key);
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => toggleModule(key)}
                  className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-xs font-medium transition-all ${isChecked
                    ? "bg-blue-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                >
                  <span
                    className={`w-4 h-4 rounded border flex items-center justify-center ${isChecked ? "bg-white border-white" : "border-gray-400"
                      }`}
                  >
                    {isChecked && (
                      <svg className="w-3 h-3 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </span>
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        {/* 저장 버튼 - Fixed Bottom */}
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t z-30">
          <div className="max-w-[480px] mx-auto">
            <button
              onClick={handleSave}
              disabled={saving}
              className="w-full btn-save flex items-center justify-center gap-2 py-3 text-sm font-medium"
            >
              <Save className="w-4 h-4" />
              {saving ? "저장 중..." : "저장"}
            </button>
          </div>
        </div>
      </div>

      {/* 주소 검색 모달 */}
      {showAddressModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* 배경 오버레이 */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowAddressModal(false)}
          />

          {/* 모달 컨텐츠 */}
          <div className="relative bg-white rounded-xl shadow-2xl w-[calc(100%-32px)] max-w-[400px] h-[70vh] max-h-[500px] flex flex-col overflow-hidden">
            {/* 모달 헤더 */}
            <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50">
              <h3 className="text-sm font-semibold text-gray-800">주소 검색</h3>
              <button
                onClick={() => setShowAddressModal(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-200 transition-colors"
              >
                <span className="text-gray-500 text-xl leading-none">&times;</span>
              </button>
            </div>

            {/* 다음 주소 검색 embed 영역 */}
            <div
              ref={addressLayerRef}
              className="flex-1 w-full"
            />
          </div>
        </div>
      )}
    </div>
  );
}